import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { SparklesIcon } from "@heroicons/react/24/solid";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import iByImdUrl from "../../assets/icons/i-by-imd.svg";
import { stripRedundantSupCaret, type ChatMessage, type ChatMode } from "./helpers.js";

export function AiAvatar({ mode }: { mode?: ChatMode | null }) {
  if (mode === "chat_article") {
    return <img src={iByImdUrl} alt="IbyIMD" className="h-8 w-8 shrink-0 rounded-full shadow-sm" />;
  }
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-lac-leman to-lac-leman-80 shadow-sm">
      <SparklesIcon className="h-4 w-4 text-white" />
    </div>
  );
}

export function TypingIndicator({ mode }: { mode?: ChatMode | null }) {
  return (
    <div className="flex items-end gap-2">
      <AiAvatar mode={mode} />
      <div className="flex items-center gap-1 rounded-2xl rounded-bl-none bg-white px-4 py-3 shadow-sm">
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-60" style={{ animationDelay: "0ms" }} />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-60" style={{ animationDelay: "150ms" }} />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-60" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  );
}

export function MessageBubble({ message, mode }: { message: ChatMessage; mode?: ChatMode | null }) {
  const isUser = message.kind === "human";
  if (message.kind === "error") {
    return (
      <div className="flex justify-end">
        <div className="border-amber-200 bg-amber-50 text-amber-800 flex max-w-[75%] items-center gap-2 rounded-xl border px-3 py-2 text-xs">
          <ExclamationTriangleIcon className="text-amber-600 h-6 w-6 shrink-0" strokeWidth={2} />
          <span className="text-left">{message.text}</span>
        </div>
      </div>
    );
  }
  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[75%] rounded-2xl rounded-br-none bg-lac-leman px-4 py-2.5 text-sm text-white">
          {message.text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-end gap-2">
      <AiAvatar mode={mode} />
      <div className="prose prose-sm min-w-0 max-w-[75%] rounded-2xl rounded-bl-none bg-white px-4 py-2.5 text-midnight shadow-sm prose-headings:mb-1 prose-headings:mt-3 prose-headings:text-sm prose-headings:font-semibold prose-h1:text-lg prose-h2:text-base prose-h3:text-[15px] [&_li]:my-0.5 [&_ol]:my-1 [&_p]:my-1 [&_ul]:my-1">
        <Markdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw, rehypeSanitize]}
          components={{
            table: ({ node: _node, ...props }) => (
              <div className="overflow-x-auto">
                <table className="w-auto" {...props} />
              </div>
            ),
          }}>
          {stripRedundantSupCaret(message.text)}
        </Markdown>
        {message.relatedSources && message.relatedSources.length > 0 && (
          <div className="not-prose mt-2 border-t border-gray/60 pt-2">
            <p className="text-xs font-medium text-gray-80">Sources</p>
            <ol className="mt-1 flex flex-col gap-1">
              {message.relatedSources.map((source, i) => (
                <li key={i} className="flex gap-1 text-xs">
                  <span className="text-gray-80">{i + 1}.</span>
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lac-leman underline hover:no-underline">
                    {source.title}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}
